import { useMemo } from 'react';

// Module-level flag so concurrent 401 responses only trigger one refresh attempt
let refreshPromise = null;

const AUTH_STORAGE_KEYS = ['token', 'username', 'roles', 'personId', 'personUuid', 'tennis04Id', 'name'];

const clearAuthStorage = () => AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

const tryRefresh = (apiUrl) => {
  if (!refreshPromise) {
    refreshPromise = fetch(`${apiUrl}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    }).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

export const resolveApiUrl = () => {
  const configuredApiUrl = window.__ENV__?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  if (configuredApiUrl) {
    return configuredApiUrl.replace(/\/$/, '');
  }

  // Standardmäßig immer relativ auflösen, damit sowohl nginx in Produktion
  // als auch der npm-Dev-Proxy in der lokalen Entwicklung funktionieren.
  // Das ist insbesondere wichtig für Remote-Tests über die IP/den Hostnamen
  // der Entwicklermaschine, weil "localhost" dann auf das Testgerät zeigen würde.
  return '/api';
};

const useApiStable = () => {
  const apiUrl = resolveApiUrl();

  const getHeaders = (includeJsonContentType = true) => {
    const headers = {
      Accept: 'application/json',
    };

    if (includeJsonContentType) {
      headers['Content-Type'] = 'application/json';
    }

    return headers;
  };

  const parseBodySafe = async (response) => {
    if (response.status === 204 || response.status === 205) {
      return null;
    }

    const contentType = (response.headers.get('content-type') || '').toLowerCase();

    if (contentType.includes('application/json')) {
      try {
        return await response.json();
      } catch {
        const txt = await response.text();
        throw new Error(`Antwort konnte nicht als JSON gelesen werden. Inhalt: ${txt.substring(0, 120)}...`);
      }
    }

    const rawText = await response.text();
    if (rawText.trim().startsWith('<!DOCTYPE') || contentType.includes('text/html')) {
      throw new Error(
        `Unerwartete HTML-Antwort erhalten (Status ${response.status}). Ausschnitt: ${rawText.substring(0, 120)}...`
      );
    }

    return rawText;
  };

  const extractErrorDetail = async (response, fallbackMessage) => {
    let errorDetail = fallbackMessage;
    try {
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      if (contentType.includes('application/json')) {
        const errJson = await response.json();
        errorDetail = errJson.message || errJson.error || JSON.stringify(errJson);
      } else {
        const txt = await response.text();
        if (txt) errorDetail = txt.substring(0, 200);
      }
    } catch {
      // fallback message bleibt erhalten
    }
    return errorDetail;
  };

  const handleAuthFailure = async (response) => {
    clearAuthStorage();
    window.dispatchEvent(new Event('auth-invalidated'));

    let detail = 'Nicht autorisiert - bitte erneut einloggen.';
    if (response) {
      detail = await extractErrorDetail(response, detail);
    }

    throw new Error(detail);
  };

  const fetchRaw = async (endpoint, options = {}, isRetry = false) => {
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const isAuthEndpoint = normalizedEndpoint.startsWith('/auth/');
    const url = `${apiUrl}${normalizedEndpoint}`;
    const hasBody = options.body !== undefined;
    const isFormData = options.body instanceof FormData;

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...getHeaders(hasBody && !isFormData),
        ...options.headers,
      },
    });

    if (response.status === 401) {
      if (!isRetry && !isAuthEndpoint) {
        try {
          const refreshResponse = await tryRefresh(apiUrl);
          if (refreshResponse?.ok) {
            return fetchRaw(normalizedEndpoint, options, true);
          }
        } catch {
          // weiter zu auth failure
        }
      }
      if (isAuthEndpoint) {
        const fallback = `HTTP error! status: ${response.status}`;
        const errorDetail = await extractErrorDetail(response, fallback);
        throw new Error(errorDetail);
      }
      await handleAuthFailure(response);
    }

    // 403 can occur when the access_token cookie expired (browser deleted it after max-age)
    // but localStorage still holds user metadata. Attempt token refresh; if that fails too
    // the session is truly gone and we must log the user out.
    // If refresh succeeds but the retry still returns 403, it is a genuine permission error
    // (user is authenticated but lacks the required role) — do NOT log out in that case.
    if (response.status === 403 && !isRetry && !isAuthEndpoint) {
      try {
        const refreshResponse = await tryRefresh(apiUrl);
        if (refreshResponse?.ok) {
          return fetchRaw(normalizedEndpoint, options, true);
        }
      } catch {
        // weiter zu auth failure
      }
      await handleAuthFailure(response);
    }

    if (!response.ok) {
      const fallback = `HTTP error! status: ${response.status}`;
      const errorDetail = await extractErrorDetail(response, fallback);
      throw new Error(errorDetail);
    }

    return response;
  };

  const fetchApi = async (endpoint, options = {}) => {
    try {
      const response = await fetchRaw(endpoint, options);
      return await parseBodySafe(response);
    } catch (err) {
      console.error('API Fehler:', err);
      throw err;
    }
  };

  return useMemo(() => ({
    baseURL: apiUrl,
    getHeaders,
    get: (endpoint) => fetchApi(endpoint, { method: 'GET' }),
    post: (endpoint, data) =>
      data === undefined
        ? fetchApi(endpoint, { method: 'POST' })
        : fetchApi(endpoint, { method: 'POST', body: JSON.stringify(data) }),
    put: (endpoint, data) =>
      data === undefined
        ? fetchApi(endpoint, { method: 'PUT' })
        : fetchApi(endpoint, { method: 'PUT', body: JSON.stringify(data) }),
    patch: (endpoint, data) =>
      data === undefined
        ? fetchApi(endpoint, { method: 'PATCH' })
        : fetchApi(endpoint, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (endpoint) => fetchApi(endpoint, { method: 'DELETE' }),
    fetchRaw: (endpoint, options = {}) => fetchRaw(endpoint, options),
  }), [apiUrl]);
};

export default useApiStable;
